/**
 * Speaking a voice line (R12) — the one place a line's own policy is read.
 *
 * @remarks
 * `speak_voice_line` says WHEN; this module owns HOW: the once check, the
 * cooldown, the gate conditions, the D30 pool cursor, and the D55
 * conditional-text resolution — in that order, and a failed check is a
 * SILENT SKIP that consumes nothing (no pool advance, no cooldown reset, no
 * entry): the scheduler's drop semantics, voice-side.
 *
 * The cooldown clock is the transcript's own turn stamp (`cursor.turnNumber`)
 * — the same number the spoken entry will carry — so "at least N executed
 * turns between speakings" and the transcript's account of when it spoke can
 * never disagree.
 *
 * @packageDocumentation
 */

import type { RuntimeState, VoiceLineId } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { EngineInvariantError } from '../execution/engine-invariant-error.js';
import {
	type StampedNarratives,
	stampNarrativeLines,
	type TranscriptCursor,
} from '../execution/transcript-cursor.js';
import { poolIndexFor } from './pool-order.js';

/** The result of one speak attempt: advanced cursor, entries (empty on a skip), next state. */
export interface VoiceLineSpeaking extends StampedNarratives {
	readonly state: RuntimeState;
}

/**
 * Attempt to speak one declared voice line, honoring the line's own policy.
 *
 * @param voiceLineId - The line to speak
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this speaking
 * @param cursor - Where the spoken line lands in the transcript
 * @returns The speaking, or an unchanged pass-through when the policy skips
 */
export const speakVoiceLine = (
	voiceLineId: VoiceLineId,
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
): VoiceLineSpeaking => {
	const definition = bundle.indexes.voiceLinesById.get(voiceLineId);

	if (definition === undefined) {
		throw new EngineInvariantError(
			`speak_voice_line references voice line "${voiceLineId}" which is not in the bundle — the loader's semantic gate should have rejected this bundle`,
		);
	}

	const timesSpoken = state.narrative.voiceLineUsage[voiceLineId] ?? 0;

	// Once-spent lines stay spent.
	if (definition.once && timesSpoken > 0) {
		return skipped(state, cursor);
	}

	// Cooling-down lines stay quiet: fewer than `cooldownTurns` executed turns
	// have passed since the transcript last carried this line.
	const lastSpokenTurn = state.narrative.voiceLineLastSpokenTurn[voiceLineId];

	if (
		definition.cooldownTurns !== undefined &&
		lastSpokenTurn !== undefined &&
		cursor.turnNumber - lastSpokenTurn < definition.cooldownTurns
	) {
		return skipped(state, cursor);
	}

	// Gate conditions: unmet means the world is not ready for this line.
	if (!definition.conditions.every((condition) => evaluateCondition(condition, bundle, state))) {
		return skipped(state, cursor);
	}

	// The pool cursor — D30's sequential hold or D141's seeded shuffle, one
	// function deciding which entry the nth speaking picks.
	const poolEntry = definition.pool[poolIndexFor(definition, timesSpoken, state.randomness.sessionSeed)];

	if (poolEntry === undefined) {
		// Unreachable by schema (pools are min(1)) — typed for the array index.
		throw new EngineInvariantError(`voice line "${voiceLineId}" resolved to an empty pool`);
	}

	// D74 made the stamp the one resolver, so the pool entry passes through
	// as authored — the register switch happens at the boundary like every
	// other conditional reading.
	const stamped = stampNarrativeLines(bundle, state, cursor, [poolEntry], `voice:${voiceLineId}`);

	return {
		...stamped,
		state: {
			...state,
			narrative: {
				...state.narrative,
				voiceLineLastSpokenTurn: {
					...state.narrative.voiceLineLastSpokenTurn,
					[voiceLineId]: cursor.turnNumber,
				},
				voiceLineUsage: {
					...state.narrative.voiceLineUsage,
					[voiceLineId]: timesSpoken + 1,
				},
			},
		},
	};
};

/** A speak attempt the policy declined — nothing consumed, nothing said. */
const skipped = (state: RuntimeState, cursor: TranscriptCursor): VoiceLineSpeaking => ({
	cursor,
	entries: [],
	state,
});
